import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Btn, Input, Field, Switch } from '@/components/ui-ios';
import { ArrowRight, Eye, EyeOff, Wifi, ShieldCheck } from 'lucide-react';

// Chiave di localStorage usata per pre-compilare l'email all'apertura
// della pagina di login. NON memorizziamo la password (sarebbe insicuro
// e contro le best practice: lasciamo questa responsabilità al password
// manager del browser tramite l'attributo autoComplete sui campi).
const LAST_LOGIN_EMAIL_KEY = 'pc.last_login_email';

export default function LoginPage() {
  const { user, login, quickLogin, quickCheck, loading } = useAuth();
  const navigate = useNavigate();
  // L'email viene letta dal localStorage al primo render: se l'utente
  // aveva spuntato "Ricorda" l'ultima volta, la ritrova precompilata.
  // Lo wrap in try/catch perché localStorage può lanciare in contesti
  // privati/incognito o quando il quota è satura.
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem(LAST_LOGIN_EMAIL_KEY) || ''; } catch { return ''; }
  });
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  // Se l'utente aveva già spuntato "Ricorda" e abbiamo un'email
  // memorizzata, di default lasciamo il toggle ON per consistenza.
  // Altrimenti default ON (comportamento storico).
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      const target = user.role === 'portalettere' ? '/m' : '/';
      navigate(target, { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await login(email, password, remember);
      // Persistenza email lato client: salviamo (o cancelliamo) l'email
      // in localStorage in base al toggle. Questo è il pezzo che mancava
      // e che gli utenti si aspettavano: al prossimo accesso si
      // ritroveranno l'email precompilata. La password resta affidata al
      // password manager nativo del browser (vedi autoComplete sul campo).
      try {
        if (remember) {
          localStorage.setItem(LAST_LOGIN_EMAIL_KEY, email.trim());
        } else {
          localStorage.removeItem(LAST_LOGIN_EMAIL_KEY);
        }
      } catch { /* storage pieno o disabilitato -> ignora silenziosamente */ }
      toast.success('Accesso effettuato');
      const target = result?.user?.role === 'portalettere' ? '/m' : '/';
      navigate(target, { replace: true });
    } catch (err) {
      const msg = err?.userMessage || err?.response?.data?.detail || 'Credenziali non valide';
      toast.error(msg, err?.code === 'DEVICE_LOCKED' ? { duration: 8000 } : undefined);
    }
    finally { setSubmitting(false); }
  };
  const onQuick = async () => {
    setSubmitting(true);
    try {
      const result = await quickLogin();
      toast.success('Accesso rapido riuscito');
      const target = result?.user?.role === 'portalettere' ? '/m' : '/';
      navigate(target, { replace: true });
    }
    catch (err) {
      const msg = err?.userMessage || err?.response?.data?.detail || 'IP non riconosciuto';
      toast.error(msg, err?.code === 'DEVICE_LOCKED' ? { duration: 8000 } : undefined);
    }
    finally { setSubmitting(false); }
  };

  return (
    <div className="ios-bg min-h-screen flex items-center justify-center p-4">
      <div className="absolute top-6 left-6 flex items-center gap-2 text-sm text-[color:var(--text-secondary)]">
        <ShieldCheck className="h-4 w-4 text-[color:var(--brand-orange)]" /> Sistema sicuro
      </div>
      <div className="w-full max-w-md">
        {/* Header logo/title rimosso su richiesta: era ridondante col
            manifest PWA e visivamente pesante. La sottotile con la
            descrizione del gestionale viene mantenuta come subheader
            sopra la card di login per un briefing chiaro all'utente. */}
        <p className="text-center text-sm text-[color:var(--text-secondary)] mb-6" data-testid="login-tagline">
          Gestionale Raccomandate · Uffici di Posta Privata
        </p>
        <div className="ios-card p-6">
          {quickCheck?.recognized && !user && (
            <button type="button" onClick={onQuick} disabled={submitting} className="w-full mb-4 p-4 rounded-2xl bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-100 flex items-center gap-3 text-left hover:from-orange-100 hover:to-amber-100 transition-colors" data-testid="login-quick-access-button">
              <div className="h-10 w-10 rounded-xl bg-[color:var(--brand-orange)] text-white flex items-center justify-center"><Wifi className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">Accesso rapido disponibile</div>
                <div className="text-xs text-[color:var(--text-secondary)] truncate">Dispositivo riconosciuto per {quickCheck.nome || quickCheck.email}</div>
              </div>
              <ArrowRight className="h-5 w-5 text-[color:var(--brand-orange)]" />
            </button>
          )}
          {/* L'attributo `name` sul form aiuta i password manager (Chrome,
              Safari, 1Password) a "etichettare" il form e proporre il
              salvataggio della coppia email/password al submit. */}
          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form" name="postacentrale-login" autoComplete="on">
            <Field label="Email o nome utente">
              <Input
                type="text"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="tu@postacentrale.it · oppure · mario.rossi"
                testid="login-email-input"
                autoComplete="username"
                name="username"
                inputMode="email"
                spellCheck={false}
                autoCapitalize="none"
              />
            </Field>
            <Field label="Password">
              <div className="relative">
                <Input
                  type={showPwd ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="pr-12"
                  testid="login-password-input"
                  /* `current-password` è il valore standard che attiva
                     l'autofill credenziali sui form di login (vs
                     `new-password` usato nei form di registrazione).
                     Senza questo attributo i password manager spesso
                     non offrono di salvare la coppia email/password,
                     che è esattamente il bug che gli utenti riportano. */
                  autoComplete="current-password"
                  name="password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  className="absolute right-2 top-1.5 h-9 w-9 rounded-full inline-flex items-center justify-center hover:bg-zinc-100"
                  aria-label={showPwd ? 'Nascondi password' : 'Mostra password'}
                  /* Hint per LastPass/1Password: ignora questo bottone
                     così non lo confondono con un campo password. */
                  data-lpignore="true"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>
            <div className="flex items-start justify-between pt-1 gap-3">
              <div className="flex items-start gap-3 flex-1">
                <div className="pt-0.5"><Switch checked={remember} onChange={setRemember} testid="login-remember-checkbox" /></div>
                <div className="min-w-0">
                  <div className="text-sm text-zinc-800 font-medium leading-tight">Ricorda email su questo dispositivo</div>
                  <div className="text-[11px] text-[color:var(--text-secondary)] leading-snug mt-0.5">
                    Per salvare anche la password, consenti al tuo browser quando ti chiede &laquo;Salva password?&raquo;.
                  </div>
                </div>
              </div>
            </div>
            <Btn type="submit" variant="accent" size="lg" className="w-full" disabled={submitting || loading} testid="login-submit-button">
              {submitting ? 'Attendere...' : 'Accedi'} <ArrowRight className="h-4 w-4" />
            </Btn>
          </form>
        </div>
      </div>
    </div>
  );
}
